import { CSSProperties, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../auth/useAuthStore';

interface Props {
  slug: string;
  onClose: () => void;
}

const RESULTS_PATH = (slug: string) => `/p/${slug}/resultados`;

export function TechnicalAccessModal({ slug, onClose }: Props): JSX.Element {
  const navigate = useNavigate();
  const { isAuthenticated, login } = useAuthStore();

  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');

  async function handleLogin(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    setError('');
    try {
      await login({ email, password });
      navigate(RESULTS_PATH(slug));
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : 'Credenciales incorrectas. Verificá el correo y la contraseña.',
      );
    } finally {
      setLoading(false);
    }
  }

  const overlay: CSSProperties = {
    position: 'fixed', inset: 0, zIndex: 800,
    background: 'rgba(15, 23, 42, 0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 20,
    backdropFilter: 'blur(2px)',
  };

  const card: CSSProperties = {
    background: '#fff', borderRadius: 18, width: '100%', maxWidth: 420,
    boxShadow: '0 8px 48px rgba(0,0,0,0.18)',
    padding: '36px 36px 32px',
    display: 'flex', flexDirection: 'column', gap: 0,
  };

  const labelStyle: CSSProperties = {
    display: 'block', fontSize: 11.5, fontWeight: 700, color: '#6b7280',
    textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6,
  };

  const inputStyle: CSSProperties = {
    display: 'block', width: '100%', boxSizing: 'border-box',
    padding: '10px 14px', borderRadius: 10,
    border: '1.5px solid #e0e0e0', outline: 'none',
    fontSize: 14, color: '#1a1a2e', background: '#fafafa',
    transition: 'border-color 0.15s',
  };

  const primaryBtn: CSSProperties = {
    width: '100%', padding: '12px', borderRadius: 10,
    border: 'none', background: '#1a1a2e',
    color: '#fff', fontSize: 14, fontWeight: 700,
    cursor: loading ? 'not-allowed' : 'pointer',
    opacity: loading ? 0.7 : 1,
    transition: 'opacity 0.15s',
  };

  const secondaryBtn: CSSProperties = {
    width: '100%', padding: '11px', borderRadius: 10,
    border: '1.5px solid #e0e0e0', background: '#fff',
    color: '#6b7280', fontSize: 13.5, fontWeight: 600,
    cursor: 'pointer',
    transition: 'border-color 0.15s',
  };

  return (
    <div style={overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={card} role="dialog" aria-modal="true" aria-labelledby="tech-modal-title">

        {/* Header */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, background: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="3" y="11" width="18" height="11" rx="2" stroke="#fff" strokeWidth="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                Consulta Ciudadana · San Cristóbal de las Casas
              </div>
              <h2 id="tech-modal-title" style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#1a1a2e', lineHeight: 1.2 }}>
                Acceso del equipo técnico
              </h2>
            </div>
          </div>
          <p style={{ margin: 0, fontSize: 13.5, color: '#6b7280', lineHeight: 1.65 }}>
            Esta sección está destinada al equipo responsable de revisar y analizar los aportes recibidos.
          </p>
        </div>

        <hr style={{ border: 'none', borderTop: '1px solid #f0f0f0', margin: '0 0 22px' }} />

        {/* Demo autofill — solo visible en entorno de desarrollo */}
        {import.meta.env.DEV && !isAuthenticated && (
          <div style={{ marginBottom: 18, padding: '8px 12px', borderRadius: 8, background: '#fffbeb', border: '1px solid #fde68a', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ fontSize: 11.5, color: '#92400e' }}>Entorno de desarrollo</span>
            <button
              type="button"
              onClick={() => setEmail('equipo.sclc@demo.local')}
              style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #fcd34d', background: '#fef3c7', color: '#92400e', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}
            >
              Usar cuenta demo
            </button>
          </div>
        )}

        {isAuthenticated ? (
          /* Already logged in — just navigate */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button
              type="button"
              style={primaryBtn}
              onClick={() => navigate(RESULTS_PATH(slug))}
            >
              Ingresar a resultados →
            </button>
            <button type="button" style={secondaryBtn} onClick={onClose}>
              Cancelar
            </button>
          </div>
        ) : (
          /* Login form — institutional, no Wayline branding */
          <form onSubmit={e => void handleLogin(e)} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label htmlFor="tech-email" style={labelStyle}>Correo electrónico</label>
              <input
                id="tech-email"
                type="email"
                autoComplete="email"
                autoFocus
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="equipo@municipio.gob.mx"
                style={inputStyle}
                onFocus={e => { (e.target as HTMLInputElement).style.borderColor = '#1a1a2e'; }}
                onBlur={e => { (e.target as HTMLInputElement).style.borderColor = '#e0e0e0'; }}
              />
            </div>

            <div>
              <label htmlFor="tech-password" style={labelStyle}>Contraseña</label>
              <input
                id="tech-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                style={inputStyle}
                onFocus={e => { (e.target as HTMLInputElement).style.borderColor = '#1a1a2e'; }}
                onBlur={e => { (e.target as HTMLInputElement).style.borderColor = '#e0e0e0'; }}
              />
            </div>

            {error && (
              <div style={{ padding: '10px 14px', borderRadius: 9, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 13, lineHeight: 1.5 }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
              <button type="submit" style={primaryBtn} disabled={loading || !email || !password}>
                {loading ? 'Verificando…' : 'Ingresar a resultados'}
              </button>
              <button type="button" style={secondaryBtn} onClick={onClose} disabled={loading}>
                Cancelar
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
